import { cn } from "@afas-doe-maar/ui/utils";
import { Bodies, Body, Engine, Render, Runner, Vector, World } from "matter-js";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import invariant from "tiny-invariant";
import type { VoteRouteProps } from "./VoteScreen";
import { voteOptionLabels } from "~/lib/constants";
import { useVotingSession } from "~/providers/votingSession/VotingSessionContext";

// Constants for better maintainability
const PHYSICS_CONFIG = {
  gravity: 1,
  restitution: 0.7,
  friction: 0.1,
  frictionAir: 0.01,
  wallThickness: 20,
  floorOffset: 10,
  sleepThreshold: 60, // Matter.js sleep threshold
  sleepVelocity: 0.8, // Velocity threshold for sleeping bodies
} as const;

const ANIMATION_CONFIG = {
  maxBalls: 200,
  initialBalls: 100, // Number of balls to start with
  initialDelay: 2000, // 2 seconds before first ball
  minBallInterval: 200,
  maxBallInterval: 1200,
  explosionForce: 0.3,
  explosionRadius: 150,
  ballRadiusMin: 10,
  ballRadiusMax: 25,
  offScreenBuffer: 100, // Buffer before considering ball off-screen
  cleanupInterval: 1000, // Check for off-screen balls every 1s
  maxBallAge: 30000, // Maximum age of a ball before forced cleanup (30s)
} as const;

// Types
interface AnimationControls {
  start: () => void;
  stop: () => void;
  isActive: () => boolean;
}

interface TrackedBall {
  body: Body;
  createdAt: number;
}

interface ThemeColors {
  pink: string;
  green: string;
}

export const VoteResults = forwardRef<HTMLDivElement, VoteRouteProps>(function VoteResults(
  { transitionState },
  ref,
) {
  const { session } = useVotingSession();
  const sceneRef = useRef<HTMLDivElement>(null);
  const animationControlsRef = useRef<AnimationControls | null>(null);
  const cleanupTimeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set());

  invariant(session.winner, "Winner must be set");
  const winner = session.winner;

  const colors = useThemeColors();
  const ballColor = winner === "pink" ? colors.pink : colors.green;

  const { engine, dimensions, trackedBalls } = useMatterEngine(sceneRef, ballColor);

  // Helper to manage timeouts
  const addTimeout = useCallback((timeout: NodeJS.Timeout) => {
    cleanupTimeoutsRef.current.add(timeout);
    return () => {
      clearTimeout(timeout);
      cleanupTimeoutsRef.current.delete(timeout);
    };
  }, []);

  // Create explosion at position with improved performance
  const createExplosion = useCallback(
    (x: number, y: number) => {
      if (!engine) return;

      const radiusSquared = ANIMATION_CONFIG.explosionRadius ** 2;

      // Only check dynamic bodies (not walls)
      const dynamicBodies = engine.world.bodies.filter(
        (body) => !body.isStatic && body.label === "ball",
      );

      dynamicBodies.forEach((body) => {
        const dx = body.position.x - x;
        const dy = body.position.y - y;
        const distanceSquared = dx * dx + dy * dy;

        if (distanceSquared < radiusSquared && distanceSquared > 0) {
          const distance = Math.sqrt(distanceSquared);
          const forceMagnitude =
            ANIMATION_CONFIG.explosionForce * (1 - distance / ANIMATION_CONFIG.explosionRadius);

          const forceVector = Vector.create(
            (dx / distance) * forceMagnitude,
            (dy / distance) * forceMagnitude,
          );

          Body.applyForce(body, body.position, forceVector);
        }
      });

      // Haptic feedback if available
      if ("vibrate" in navigator) {
        navigator.vibrate(50);
      }
    },
    [engine],
  );

  // Handle pointer events
  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!sceneRef.current) return;

      const rect = sceneRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      createExplosion(x, y);
    },
    [createExplosion],
  );

  // Animation setup - runs once when engine is ready
  useEffect(() => {
    if (!engine || !sceneRef.current) return;

    let animationActive = false;
    let ballCount = 0;
    let intensity = 0;
    let hasReachedMaxBalls = false;
    let cleanupInterval: NodeJS.Timeout | null = null;

    // Check if ball is off-screen or too old
    const shouldRemoveBall = (ball: TrackedBall): boolean => {
      const now = Date.now();
      const age = now - ball.createdAt;

      // Remove if too old
      if (age > ANIMATION_CONFIG.maxBallAge) return true;

      // Remove if off-screen
      const { x, y } = ball.body.position;
      return (
        y > dimensions.height + ANIMATION_CONFIG.offScreenBuffer ||
        y < -ANIMATION_CONFIG.offScreenBuffer ||
        x < -ANIMATION_CONFIG.offScreenBuffer ||
        x > dimensions.width + ANIMATION_CONFIG.offScreenBuffer
      );
    };

    // Create a new ball
    const createBall = (x?: number): Body | null => {
      if (!engine || ballCount >= ANIMATION_CONFIG.maxBalls) return null;

      const ballX = x ?? Math.random() * (dimensions.width - 100) + 50;
      const radius =
        Math.random() * (ANIMATION_CONFIG.ballRadiusMax - ANIMATION_CONFIG.ballRadiusMin) +
        ANIMATION_CONFIG.ballRadiusMin;

      const ball = Bodies.circle(ballX, -50, radius, {
        restitution: PHYSICS_CONFIG.restitution,
        friction: PHYSICS_CONFIG.friction,
        frictionAir: PHYSICS_CONFIG.frictionAir,
        render: { fillStyle: ballColor },
        label: "ball",
        sleepThreshold: PHYSICS_CONFIG.sleepThreshold,
        // slop: 0.05,
      });

      World.add(engine.world, ball);
      trackedBalls.current.set(ball.id, { body: ball, createdAt: Date.now() });
      ballCount++;

      return ball;
    };

    // Cleanup and recycle balls
    const cleanupBalls = () => {
      if (!animationActive) return;

      const ballsToRemove: number[] = [];

      trackedBalls.current.forEach((trackedBall, id) => {
        if (shouldRemoveBall(trackedBall)) {
          ballsToRemove.push(id);
        }
      });

      ballsToRemove.forEach((id) => {
        const trackedBall = trackedBalls.current.get(id);

        if (trackedBall && engine.world.bodies.includes(trackedBall.body)) {
          createExplosion(trackedBall.body.position.x, trackedBall.body.position.y);
          World.remove(engine.world, trackedBall.body);
          trackedBalls.current.delete(id);
          ballCount--;

          // Recycle: create new ball if we've reached max
          if (hasReachedMaxBalls && animationActive) {
            createBall();
          }
        }
      });
    };

    // Animation control
    const startAnimation = () => {
      if (animationActive) return;

      animationActive = true;
      intensity = 0;
      ballCount = 0;
      hasReachedMaxBalls = false;

      // Setup cleanup interval
      cleanupInterval = setInterval(cleanupBalls, ANIMATION_CONFIG.cleanupInterval);

      // Create initial 100 balls, staggered with 50ms
      for (let i = 0; i < ANIMATION_CONFIG.initialBalls; i++) {
        addTimeout(
          // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
          setTimeout(() => createBall(), 50 * i),
        );
      }

      // Start dropping balls after initial delay
      // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
      const initialTimeout = setTimeout(() => {
        if (!animationActive) return;

        const dropBalls = () => {
          if (!animationActive || hasReachedMaxBalls) return;

          const ball = createBall();
          if (!ball) {
            hasReachedMaxBalls = true;
            return;
          }

          // Calculate next interval
          const nextInterval = Math.max(
            ANIMATION_CONFIG.minBallInterval,
            ANIMATION_CONFIG.maxBallInterval - intensity * 40,
          );

          intensity = Math.min(intensity + 1, 25);

          // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
          const dropTimeout = setTimeout(dropBalls, nextInterval);
          addTimeout(dropTimeout);
        };

        dropBalls();
      }, ANIMATION_CONFIG.initialDelay);

      addTimeout(initialTimeout);
    };

    const stopAnimation = () => {
      animationActive = false;

      // Clear all timeouts
      cleanupTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      cleanupTimeoutsRef.current.clear();

      // Clear cleanup interval
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
        cleanupInterval = null;
      }

      // Remove all balls
      const balls = engine.world.bodies.filter((body) => body.label === "ball");
      World.remove(engine.world, balls);
      trackedBalls.current.clear();
      ballCount = 0;
    };

    const isActive = () => animationActive;

    animationControlsRef.current = { start: startAnimation, stop: stopAnimation, isActive };

    return () => {
      stopAnimation();
    };
  }, [engine, ballColor, dimensions, trackedBalls, addTimeout, createExplosion]);

  // Handle transition state changes - separate effect to avoid restarting animation
  useEffect(() => {
    const controls = animationControlsRef.current;
    if (!controls) return;

    if (transitionState === "entered" || transitionState === "entering") {
      controls.start();
    } else if (transitionState === "exiting" || transitionState === "exited") {
      controls.stop();
    }
  }, [transitionState]);

  return (
    <div ref={ref} className="grid *:col-span-full *:row-span-full">
      <div
        ref={sceneRef}
        className="h-full w-full"
        onPointerDown={handlePointerDown}
        onContextMenu={(e) => e.preventDefault()} // Prevent context menu on long press
        style={{ touchAction: "none" }}
        role="application"
        aria-label={`Vieringsanimatie voor overwinning van team ${winner}. Klik of druk op spatie voor explosies.`}
        aria-roledescription="Interactieve animatie"
      />

      <div className="from-background/20 pointer-events-none z-10 bg-linear-to-t to-transparent" />

      <div
        className={cn(
          "pointer-events-none z-20 flex -translate-x-full flex-col items-center justify-center gap-4 opacity-0 transition-[translate,opacity] duration-500",
          {
            entering: "translate-x-0 opacity-100",
            entered: "translate-x-0 opacity-100",
            exiting: "translate-x-full opacity-0",
            exited: "-translate-x-full opacity-0 duration-0",
            unmounted: "",
          }[transitionState],
        )}
      >
        <p
          className="font-heading bg-background text-yellow px-4 py-1 text-3xl font-bold text-balance select-none"
          style={{ clipPath: "polygon(16px 0, 100% 0, calc(100% - 16px) 100%, 0 100%)" }}
        >
          {`${voteOptionLabels[winner].toUpperCase()} WINT!`}
        </p>
      </div>
    </div>
  );
});

function useThemeColors(): ThemeColors {
  return useMemo(() => {
    const rootStyles = getComputedStyle(document.documentElement);
    return {
      pink: rootStyles.getPropertyValue("--color-pink").trim() || "#ff1493",
      green: rootStyles.getPropertyValue("--color-green").trim() || "#00ff00",
    };
  }, []);
}

function useMatterEngine(containerRef: React.RefObject<HTMLDivElement>, ballColor: string) {
  const engineRef = useRef<Engine | null>(null);
  const renderRef = useRef<Render | null>(null);
  const runnerRef = useRef<Runner | null>(null);
  const trackedBallsRef = useRef<Map<number, TrackedBall>>(new Map());
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [engineKey, setEngineKey] = useState(0); // Key to force recreation

  // Initialize engine
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.offsetWidth || window.innerWidth;
    const height = container.offsetHeight || window.innerHeight;

    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setDimensions({ width, height });

    // Create engine with optimized settings
    const engine = Engine.create({
      enableSleeping: true,
    });
    engineRef.current = engine;
    engine.world.gravity.y = PHYSICS_CONFIG.gravity;

    // Create renderer
    const render = Render.create({
      element: container,
      engine: engine,
      options: {
        width,
        height,
        wireframes: false,
        background: "transparent",
        showAngleIndicator: false,
        showVelocity: false,
        showSleeping: false,
        showDebug: false,
        pixelRatio: Math.min(window.devicePixelRatio || 1, 2), // Cap at 2 for performance
      },
    });
    renderRef.current = render;

    // Create boundaries
    const walls = [
      // Floor
      Bodies.rectangle(
        width / 2,
        height - PHYSICS_CONFIG.floorOffset,
        width,
        PHYSICS_CONFIG.wallThickness,
        {
          isStatic: true,
          render: { fillStyle: "transparent" },
          label: "floor",
        },
      ),
      // Left wall
      Bodies.rectangle(
        PHYSICS_CONFIG.floorOffset,
        height / 2,
        PHYSICS_CONFIG.wallThickness,
        height * 2,
        {
          isStatic: true,
          render: { fillStyle: "transparent" },
          label: "leftWall",
        },
      ),
      // Right wall
      Bodies.rectangle(
        width - PHYSICS_CONFIG.floorOffset,
        height / 2,
        PHYSICS_CONFIG.wallThickness,
        height * 2,
        {
          isStatic: true,
          render: { fillStyle: "transparent" },
          label: "rightWall",
        },
      ),
    ];

    World.add(engine.world, walls);

    // Start renderer and runner
    Render.run(render);
    const runner = Runner.create();
    runnerRef.current = runner;
    Runner.run(runner, engine);

    return () => {
      // Cleanup Matter.js
      if (render) {
        Render.stop(render);
        render.canvas?.remove();
      }

      if (runner && engine) {
        Runner.stop(runner);
        World.clear(engine.world, false);
        Engine.clear(engine);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
      trackedBallsRef.current.clear();
      container.innerHTML = "";
    };
  }, [containerRef, ballColor, engineKey]); // Added engineKey dependency

  // Handle resize/orientation change - restart animation
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Force engine recreation by changing the key
        setEngineKey((prev) => prev + 1);
      }, 250);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  return {
    engine: engineRef.current,
    dimensions,
    trackedBalls: trackedBallsRef,
  };
}
